import { ArrowLeftOutlined, FileExcelOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Select, Space, Spin, Table } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ContentHeader } from '../../components';
import Summernote from '../../components/summernote/Summernote';
import { dateCommon } from '../../helpers/common';
import { Links } from '../../helpers/Links';
import { BaoCaoKiemKe } from '../../models/taisan/TaiSan';
import { LuuBienBanKiemKeRequest } from '../../models/taisan/BienBanKiemKe';
import { DataSelectBox } from '../../models/selectbox';
import {
    layChiTietBienBanKiemKe,
    layBangTongHopKiemKe,
    luuBienBanKiemKe,
    xuatExcelBienBanKiemKePhongBan,
} from '../../services/bienBanKiemKe';
import { layChiTietDotKiemKe } from '../../services/dotKiemKeTaiSan';
import { layDsToanBoDonVi } from '../../services/donvi';
import DonViSelectTree from '../utils/DonViSelectTree';

const ChiTietBienBanKiemKe = () => {
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id') ? parseInt(params.get('id')!) : undefined;
    const idDotKiemKe = params.get('idDotKiemKe') ? parseInt(params.get('idDotKiemKe')!) : undefined;

    const isThemMoi = !id;

    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [loadingChiTiet, setLoadingChiTiet] = useState(false);
    const [loadingBang, setLoadingBang] = useState(false);
    const [noiDung, setNoiDung] = useState('');
    const [dsBangTongHop, setDsBangTongHop] = useState<BaoCaoKiemKe[]>([]);
    const [optionDonVi, setOptionDonVi] = useState<DataSelectBox[]>([]);
    const [idDotKiemKeTruoc, setIdDotKiemKeTruoc] = useState<number | undefined>();
    const [ngayKetThuc, setNgayKetThuc] = useState('');
    const [nam, setNam] = useState<string | number>('');
    const summernoteKey = useRef(0);

    const loadDsDonVi = async () => {
        try {
            const data = await layDsToanBoDonVi();
            setOptionDonVi((data || []).map((dv: any) => ({ value: dv.id, label: dv.ten })));
        } catch (e) {
            console.error(e);
        }
    };

    const loadThongTinDot = async () => {
        if (!idDotKiemKe) return;
        try {
            const dotRaw = await layChiTietDotKiemKe(idDotKiemKe);
            const dot = dotRaw as any;
            if (dot) {
                setIdDotKiemKeTruoc(dot.idDotKiemKeTruoc);
                if (dot.ngayKetThuc) {
                    setNgayKetThuc(dateCommon.formatDate(dot.ngayKetThuc));
                    setNam(new Date(dot.ngayKetThuc).getFullYear());
                }
            }
        } catch (e) {
            console.error(e);
        }
    };

    const loadChiTiet = async () => {
        if (!id) return;
        try {
            setLoadingChiTiet(true);
            const ct = await layChiTietBienBanKiemKe(id);
            if (ct) {
                const bb = ct as any;
                form.setFieldsValue({
                    tenBienBan: bb.tenBienBan,
                    idDonVi: bb.idDonVi,
                });
                setNoiDung(bb.noiDung || '');
                summernoteKey.current += 1;
            }
        } catch (err: any) {
            toast.error('Lỗi khi tải chi tiết biên bản: ' + err.message);
        } finally {
            setLoadingChiTiet(false);
        }
    };

    useEffect(() => {
        loadDsDonVi();
        loadThongTinDot();
        if (id) loadChiTiet();
    }, []);

    const handleLayBangTongHop = async () => {
        if (!idDotKiemKe) {
            toast.warning('Không tìm thấy đợt kiểm kê');
            return;
        }
        const idDonViChon = form.getFieldValue('idDonVi');
        try {
            setLoadingBang(true);
            const data = await layBangTongHopKiemKe(idDotKiemKe, idDonViChon, idDotKiemKeTruoc);
            setDsBangTongHop(data);
        } catch (err: any) {
            toast.error('Lỗi khi tải bảng tổng hợp: ' + err.message);
        } finally {
            setLoadingBang(false);
        }
    };

    const handleXuatExcel = async () => {
        if (!idDotKiemKe) {
            toast.warning('Không tìm thấy đợt kiểm kê');
            return;
        }
        const idDonViChon = form.getFieldValue('idDonVi');
        try {
            await xuatExcelBienBanKiemKePhongBan(idDotKiemKe, idDonViChon);
        } catch (err: any) {
            toast.error('Lỗi khi xuất excel: ' + err.message);
        }
    };

    const buildHtmlTable = (data: BaoCaoKiemKe[]) => {
        const rows = data.map(r => `
            <tr>
                <td style="text-align:center;border:1px solid #000;padding:4px">${r.stt}</td>
                <td style="border:1px solid #000;padding:4px">${r.tenLoaiTaiSan || ''}</td>
                <td style="text-align:center;border:1px solid #000;padding:4px">${r.soLuongKyTruoc || ''}</td>
                <td style="text-align:center;border:1px solid #000;padding:4px">${r.soLuongTang > 0 ? r.soLuongTang : ''}</td>
                <td style="text-align:center;border:1px solid #000;padding:4px">${r.soLuongGiam > 0 ? r.soLuongGiam : ''}</td>
                <td style="text-align:center;border:1px solid #000;padding:4px">${r.soLuongHienTai ?? ''}</td>
                <td style="border:1px solid #000;padding:4px">${r.danhSachMa || ''}</td>
                <td style="text-align:center;border:1px solid #000;padding:4px">${r.dangSuDung > 0 ? 'X' : ''}</td>
                <td style="text-align:center;border:1px solid #000;padding:4px">${r.khongSuDung > 0 ? 'X' : ''}</td>
                <td style="border:1px solid #000;padding:4px">${r.ghiChu || ''}</td>
            </tr>`).join('');

        return `
<table style="width:100%;border-collapse:collapse;font-size:12px">
    <thead>
        <tr>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold" rowspan="2">STT</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold" rowspan="2">Tên loại TSCĐ, Công cụ dụng cụ</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold" rowspan="2">Thực tế đến ngày ${ngayKetThuc}</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold" colspan="2">Số lượng TSCĐ, CCDC</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold" rowspan="2">Số lượng thực tế kiểm kê ngày ${ngayKetThuc}</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold" rowspan="2">Mã số (nếu có)</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold" colspan="2">Tình trạng</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold" rowspan="2">Ghi chú</th>
        </tr>
        <tr>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold">Tăng trong năm ${nam}</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold">Giảm trong năm ${nam}</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold">Đang sử dụng</th>
            <th style="text-align:center;border:1px solid #000;padding:4px;font-weight:bold">Không sử dụng</th>
        </tr>
        <tr style="font-style:italic;text-align:center;background:#f5f5f5">
            <td style="border:1px solid #000;padding:2px">A</td>
            <td style="border:1px solid #000;padding:2px">B</td>
            <td style="border:1px solid #000;padding:2px">C</td>
            <td style="border:1px solid #000;padding:2px">D</td>
            <td style="border:1px solid #000;padding:2px">E</td>
            <td style="border:1px solid #000;padding:2px">F=C+D-E</td>
            <td style="border:1px solid #000;padding:2px">G</td>
            <td style="border:1px solid #000;padding:2px">H</td>
            <td style="border:1px solid #000;padding:2px">I</td>
            <td style="border:1px solid #000;padding:2px">K</td>
        </tr>
    </thead>
    <tbody>
        ${rows}
    </tbody>
</table>`;
    };

    const handleChuyenVaoSummernote = () => {
        if (dsBangTongHop.length === 0) {
            toast.warning('Chưa có dữ liệu bảng tổng hợp. Vui lòng tải dữ liệu trước.');
            return;
        }
        const html = buildHtmlTable(dsBangTongHop);
        setNoiDung(prev => prev + html);
        summernoteKey.current += 1;
        toast.success('Đã chuyển bảng tổng hợp vào nội dung');
    };

    const handleLuu = async () => {
        try {
            const values = await form.validateFields();
            if (!idDotKiemKe) {
                toast.error('Không tìm thấy đợt kiểm kê');
                return;
            }
            setSaving(true);
            const request: LuuBienBanKiemKeRequest = {
                id,
                idDotKiemKe,
                idDonVi: values.idDonVi,
                tenBienBan: values.tenBienBan,
                noiDung,
            };
            await luuBienBanKiemKe(request);
            toast.success(isThemMoi ? 'Tạo biên bản thành công' : 'Cập nhật biên bản thành công');
            navigate(-1);
        } catch (err: any) {
            if (err?.errorFields) return;
            toast.error('Lỗi khi lưu: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const bangTongHopColumns = [
        { title: 'STT', dataIndex: 'stt', key: 'stt', width: 50, align: 'center' as const },
        { title: 'Tên TSCĐ/CCDC', dataIndex: 'tenLoaiTaiSan', key: 'tenLoaiTaiSan' },
        {
            title: ngayKetThuc ? `Thực tế đến ${ngayKetThuc}` : 'SL kỳ trước',
            dataIndex: 'soLuongKyTruoc',
            key: 'soLuongKyTruoc',
            width: 110,
            align: 'center' as const,
        },
        {
            title: nam ? `Tăng trong năm ${nam}` : 'Tăng',
            dataIndex: 'soLuongTang',
            key: 'soLuongTang',
            width: 100,
            align: 'center' as const,
            render: (v: number) => v > 0 ? v : '',
        },
        {
            title: nam ? `Giảm trong năm ${nam}` : 'Giảm',
            dataIndex: 'soLuongGiam',
            key: 'soLuongGiam',
            width: 100,
            align: 'center' as const,
            render: (v: number) => v > 0 ? v : '',
        },
        {
            title: ngayKetThuc ? `SL KK ngày ${ngayKetThuc}` : 'SL thực tế KK',
            dataIndex: 'soLuongHienTai',
            key: 'soLuongHienTai',
            width: 120,
            align: 'center' as const,
        },
        { title: 'Mã số', dataIndex: 'danhSachMa', key: 'danhSachMa', width: 180 },
        {
            title: 'Đang SD',
            dataIndex: 'dangSuDung',
            key: 'dangSuDung',
            width: 75,
            align: 'center' as const,
            render: (v: number) => (v > 0 ? 'X' : ''),
        },
        {
            title: 'Không SD',
            dataIndex: 'khongSuDung',
            key: 'khongSuDung',
            width: 80,
            align: 'center' as const,
            render: (v: number) => (v > 0 ? 'X' : ''),
        },
    ];

    if (loadingChiTiet) {
        return (
            <section>
                <ContentHeader title={isThemMoi ? 'Thêm mới biên bản kiểm kê' : 'Chỉnh sửa biên bản kiểm kê'} />
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
                            <Spin size="large" />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section>
            <ContentHeader title={isThemMoi ? 'Thêm mới biên bản kiểm kê' : 'Chỉnh sửa biên bản kiểm kê'} />
            <div className="container-fluid">
                <div className="card">
                    <div className="card-body">
                        <Form form={form} layout="vertical">
                            <Row gutter={16}>
                                <Col span={14}>
                                    <Form.Item
                                        label="Tên biên bản"
                                        name="tenBienBan"
                                        rules={[{ required: true, message: 'Vui lòng nhập tên biên bản' }]}
                                    >
                                        <Input placeholder="Nhập tên biên bản..." />
                                    </Form.Item>
                                </Col>
                                <Col span={10}>
                                    <Form.Item
                                        label="Đơn vị"
                                        name="idDonVi"
                                        rules={[{ required: true, message: 'Vui lòng chọn đơn vị' }]}
                                    >
                                        <DonViSelectTree name="idDonVi" donViQuanLy />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>

                        {/* Bảng tổng hợp */}
                        <div style={{ marginBottom: 20, padding: 12, background: '#fafafa', borderRadius: 6, border: '1px solid #e8e8e8' }}>
                            <Row justify="space-between" align="middle" style={{ marginBottom: 10 }}>
                                <Col><strong>Bảng tổng hợp kiểm kê theo đơn vị</strong></Col>
                                <Col>
                                    <Space>
                                        <Button
                                            size="small"
                                            icon={<ReloadOutlined />}
                                            onClick={handleLayBangTongHop}
                                            loading={loadingBang}
                                        >
                                            Tải dữ liệu theo đơn vị đã chọn
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<FileExcelOutlined />}
                                            style={{ color: '#28a745' }}
                                            onClick={handleXuatExcel}
                                        >
                                            Xuất Excel (Biểu mẫu b)
                                        </Button>
                                        <Button
                                            size="small"
                                            type="dashed"
                                            onClick={handleChuyenVaoSummernote}
                                            disabled={dsBangTongHop.length === 0}
                                        >
                                            Chuyển dữ liệu bảng vào nội dung ↓
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                            <Spin spinning={loadingBang}>
                                <Table
                                    columns={bangTongHopColumns}
                                    dataSource={dsBangTongHop}
                                    rowKey="stt"
                                    size="small"
                                    pagination={false}
                                    scroll={{ x: 900 }}
                                    locale={{ emptyText: 'Chọn đơn vị và nhấn "Tải dữ liệu" để xem bảng tổng hợp' }}
                                />
                            </Spin>
                        </div>

                        {/* Summernote nội dung */}
                        <div style={{ marginBottom: 20 }}>
                            <div style={{ marginBottom: 6, fontWeight: 500 }}>Nội dung biên bản</div>
                            <Summernote
                                key={summernoteKey.current}
                                value={noiDung}
                                onChange={setNoiDung}
                                placeholder="Nhập nội dung biên bản (Quyết định, Tổ kiểm kê, Đại diện bộ phận sử dụng...)"
                                minHeight={400}
                            />
                        </div>

                        {/* Actions */}
                        <Row justify="end">
                            <Space>
                                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                                    Quay lại
                                </Button>
                                <Button
                                    icon={<FileExcelOutlined />}
                                    style={{ color: '#28a745' }}
                                    onClick={handleXuatExcel}
                                >
                                    Xuất Excel
                                </Button>
                                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleLuu}>
                                    Lưu biên bản
                                </Button>
                            </Space>
                        </Row>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ChiTietBienBanKiemKe;
